import { SortDirection } from '@repo/shared/dtos/beneficiary'
import { StructuredDataSchemaItem } from '@repo/shared/dtos/structured-data'
import type { Table } from '@tanstack/react-table'
import { ReactNode, useCallback, useMemo } from 'react'

import { DataTable } from '@/components/admin/members/data-table'
import { useBeneficiaryListTableColumns } from '@/components/beneficiary/list/table/columns'
import { useBeneficiaryListTableData } from '@/components/beneficiary/list/table/data'
import { Beneficiary } from '@/store/beneficiary'
import { setSortOption } from '@/store/beneficiary-list-filters/slice'
import { useStoreDispatch } from '@/store/hooks'
import { Organization } from '@/store/user'

export type BeneficiaryListDataItem = {
  id: string
  fullName: string
  profile: string
  [key: string]: StructuredDataSchemaItem['defaultValue']
}

export type BeneficiaryListTableChildren = {
  children?: (table: Table<BeneficiaryListDataItem>) => ReactNode
}

export type BeneficiaryListTableProps = {
  beneficiaries: Beneficiary[]
  onSelectBeneficiary?: () => void
  organization: Organization
  sortOption: {
    direction: SortDirection
    field: string
  }
} & BeneficiaryListTableChildren

export function BeneficiaryListTable({
  beneficiaries,
  onSelectBeneficiary,
  organization,
  sortOption: sortOption,
  children,
}: BeneficiaryListTableProps) {
  const dispatch = useStoreDispatch()
  const handleSorting = useCallback(
    (sorting: { id: string; desc: boolean }[]) => {
      const firstItem = sorting[0]
      if (!firstItem) return

      const newSortField = firstItem.id
      const newSortDirection: SortDirection = firstItem.desc ? 'DESC' : 'ASC'

      if (newSortDirection === sortOption.direction && newSortField === sortOption.field) return

      dispatch(setSortOption({ direction: newSortDirection, field: newSortField }))
    },
    [sortOption],
  )

  const data = useBeneficiaryListTableData({ beneficiaries, organization })
  const columns = useBeneficiaryListTableColumns({
    organization,
    onSelectBeneficiary,
    data,
  })

  const initialState = useMemo(
    () => ({
      sorting: [{ id: sortOption.field, desc: sortOption.direction === 'DESC' }],
      columnVisibility: columns.reduce(
        (acc, col) => {
          if (
            'meta' in col &&
            typeof col.meta === 'object' &&
            'defaultHidden' in col.meta &&
            col.meta?.defaultHidden === true
          ) {
            acc[col.id as string] = false
          }
          return acc
        },
        {} as Record<string, boolean>,
      ),
    }),
    [sortOption, columns],
  )

  return (
    <DataTable
      initialState={initialState}
      columns={columns}
      data={data}
      manualSorting={true}
      onSortingChange={handleSorting}
      header={children}
    />
  )
}
